import React, { useState, useEffect, useCallback } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import Select from 'react-select';
//@ts-ignore
import Modal from 'react-awesome-modal';
import { SvgElement, icontypesEnum } from '../assets/svgElement';

function Reimbursements() {
    const userLoginData = useSelector((state: any) => state.data.login.value);
    const userPermissions: string[] = userLoginData?.data?.permissions
        ? JSON.parse(userLoginData.data.permissions)
        : [];
    const isAdmin = userLoginData?.data?.role === 'admin';

    const canInitiate = isAdmin || userPermissions.includes('can_initiate_reimbursement');
    const canApproveL1 = isAdmin || userPermissions.includes('can_approve_reimbursement_l1');
    const canApproveL2 = isAdmin || userPermissions.includes('can_approve_reimbursement_l2');

    // Data
    const [requests, setRequests] = useState<any>([]);
    const [summary, setSummary] = useState<any>(null);
    const [loader, setLoader] = useState(false);
    const [actionLoader, setActionLoader] = useState(false);

    // Filters
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Create modal
    const [createModal, setCreateModal] = useState(false);
    const [fleetDropdown, setFleetDropdown] = useState<any[]>([]);
    const [fleetDropdownLoader, setFleetDropdownLoader] = useState(false);
    const [newRequest, setNewRequest] = useState({ fleet_user_id: '', amount: '', reason: '', reference: '' });

    // Approve modal
    const [approveModal, setApproveModal] = useState(false);
    const [approveType, setApproveType] = useState<'l1' | 'l2'>('l1');
    const [approveId, setApproveId] = useState<number | null>(null);
    const [approveNotes, setApproveNotes] = useState('');
    const [approveItem, setApproveItem] = useState<any>(null);

    // Reject modal
    const [rejectModal, setRejectModal] = useState(false);
    const [rejectId, setRejectId] = useState<number | null>(null);
    const [rejectReason, setRejectReason] = useState('');

    // Detail modal
    const [detailModal, setDetailModal] = useState(false);
    const [detailItem, setDetailItem] = useState<any>(null);

    const formatAmount = (amount: any) => '₦' + Number(amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 });

    // ── Load requests ──────────────────────────────────────
    const loadRequests = useCallback((page?: string) => {
        setLoader(true);
        const query: any = {
            search, status: statusFilter,
            date_from: dateFrom, date_to: dateTo,
            page: page || '1', per_page: '15',
        };
        SuperAdminApis.getReimbursements(query).then(
            (response: AxiosResponse<any>) => {
                setLoader(false);
                if (response?.data?.status) {
                    setRequests(response.data.data);
                    setSummary(response.data.summary);
                }
            }
        ).catch(() => { setLoader(false); toast.error('Failed to load reimbursements.'); });
    }, [search, statusFilter, dateFrom, dateTo]);

    useEffect(() => { loadRequests(); }, []);

    // ── Load fleet dropdown ────────────────────────────────
    const loadFleetDropdown = () => {
        if (fleetDropdown.length > 0) return;
        setFleetDropdownLoader(true);
        SuperAdminApis.getReimbursementFleetDropdown().then(
            (res: AxiosResponse<any>) => {
                setFleetDropdownLoader(false);
                if (res?.data?.status) setFleetDropdown(res.data.data);
            }
        ).catch(() => { setFleetDropdownLoader(false); });
    };

    // ── Create ─────────────────────────────────────────────
    const handleCreate = () => {
        if (!newRequest.fleet_user_id || !newRequest.amount || !newRequest.reason) {
            toast.warn('Please fill in fleet, amount, and reason.');
            return;
        }
        setActionLoader(true);
        SuperAdminApis.createReimbursement(newRequest).then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            if (res?.data?.status) {
                toast.success(res.data.message);
                setCreateModal(false);
                setNewRequest({ fleet_user_id: '', amount: '', reason: '', reference: '' });
                loadRequests();
            } else {
                toast.error(res?.data?.message || 'Failed to create.');
            }
        }).catch((err: any) => {
            setActionLoader(false);
            toast.error(err?.response?.data?.message || 'Failed to create.');
        });
    };

    // ── Approve ────────────────────────────────────────────
    const openApprove = (item: any, type: 'l1' | 'l2') => {
        setApproveItem(item);
        setApproveId(item.id);
        setApproveType(type);
        setApproveNotes('');
        setApproveModal(true);
    };

    const handleApprove = () => {
        if (!approveId) return;
        setActionLoader(true);
        const fn = approveType === 'l1'
            ? SuperAdminApis.approveReimbursementL1(approveId, { notes: approveNotes })
            : SuperAdminApis.approveReimbursementL2(approveId, { notes: approveNotes });

        fn.then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            if (res?.data?.status) {
                toast.success(res.data.message);
                setApproveModal(false);
                loadRequests();
            } else {
                toast.error(res?.data?.message || 'Approval failed.');
            }
        }).catch((err: any) => {
            setActionLoader(false);
            toast.error(err?.response?.data?.message || 'Approval failed.');
        });
    };

    // ── Reject ─────────────────────────────────────────────
    const handleReject = () => {
        if (!rejectReason) { toast.warn('Please provide a reason.'); return; }
        if (!rejectId) return;
        setActionLoader(true);
        SuperAdminApis.rejectReimbursement(rejectId, { reason: rejectReason }).then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            setRejectModal(false);
            setRejectReason('');
            if (res?.data?.status) { toast.success('Reimbursement rejected.'); loadRequests(); }
            else { toast.error(res?.data?.message || 'Failed.'); }
        }).catch((err: any) => {
            setActionLoader(false);
            toast.error(err?.response?.data?.message || 'Failed.');
        });
    };

    // ── Helpers ─────────────────────────────────────────────
    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'approved_l1': return 'bg-blue-100 text-blue-800';
            case 'completed': return 'bg-green-100 text-green-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'pending': return 'Pending L1';
            case 'approved_l1': return 'Awaiting L2';
            case 'completed': return 'Completed';
            case 'rejected': return 'Rejected';
            default: return status;
        }
    };

    const paginate = (label: string) => {
        const page = label === 'Next &raquo;' || label === '&laquo; Previous'
            ? requests?.links?.find((l: any) => l.label === label)?.url?.split('page=')[1]
            : label;
        if (page) loadRequests(page);
    };

    return (
        <>
            <AdminSidebar title="Reimbursements" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Reimbursements" />

                <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">
                    <div className="flex justify-between items-center mt-5 mb-2">
                        <div>
                            <h3 className="text-[15px] text-[#1C1C1C] font-semibold">Fleet Reimbursements</h3>
                            <p className="text-xs text-gray-500">Add funds to fleet agent wallets with 2-level approval.</p>
                        </div>
                        {canInitiate && (
                            <button onClick={() => { setCreateModal(true); loadFleetDropdown(); }}
                                className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">
                                + New Reimbursement
                            </button>
                        )}
                    </div>

                    {/* ── Summary Cards ─────────────────────────────── */}
                    {summary && (
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
                            <div className="bg-white rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition"
                                onClick={() => { setStatusFilter('pending'); setTimeout(() => loadRequests(), 0); }}>
                                <p className="text-xs text-gray-500">Pending L1</p>
                                <p className="text-xl font-bold text-yellow-600">{summary.pending}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition"
                                onClick={() => { setStatusFilter('approved_l1'); setTimeout(() => loadRequests(), 0); }}>
                                <p className="text-xs text-gray-500">Awaiting L2</p>
                                <p className="text-xl font-bold text-blue-600">{summary.awaiting_l2}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition"
                                onClick={() => { setStatusFilter('completed'); setTimeout(() => loadRequests(), 0); }}>
                                <p className="text-xs text-gray-500">Completed</p>
                                <p className="text-xl font-bold text-green-600">{summary.completed}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition"
                                onClick={() => { setStatusFilter('rejected'); setTimeout(() => loadRequests(), 0); }}>
                                <p className="text-xs text-gray-500">Rejected</p>
                                <p className="text-xl font-bold text-red-600">{summary.rejected}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Total Credited</p>
                                <p className="text-lg font-bold text-green-700">{formatAmount(summary.total_completed_amount)}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">In Pipeline</p>
                                <p className="text-lg font-bold text-orange-600">{formatAmount(summary.total_pending_amount)}</p>
                            </div>
                        </div>
                    )}

                    {/* ── Filters ───────────────────────────────────── */}
                    <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                        <div className="flex flex-wrap gap-3 items-end">
                            <div className="flex-1 min-w-[150px]">
                                <label className="text-xs text-gray-500">Search</label>
                                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Fleet name, reason, reference..."
                                    className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <div className="min-w-[140px]">
                                <label className="text-xs text-gray-500">Status</label>
                                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                    <option value="all">All</option>
                                    <option value="pending">Pending L1</option>
                                    <option value="approved_l1">Awaiting L2</option>
                                    <option value="completed">Completed</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>
                            <div className="min-w-[130px]">
                                <label className="text-xs text-gray-500">From</label>
                                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <div className="min-w-[130px]">
                                <label className="text-xs text-gray-500">To</label>
                                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <button onClick={() => loadRequests()} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">Filter</button>
                            {statusFilter !== 'all' && (
                                <button onClick={() => { setStatusFilter('all'); setTimeout(() => loadRequests(), 0); }}
                                    className="text-[#E04343] text-xs font-semibold hover:underline">Clear</button>
                            )}
                        </div>
                    </div>

                    {/* ── Table ─────────────────────────────────────── */}
                    <div className="relative overflow-x-auto">
                        {loader && (
                            <div className="flex justify-center py-16">
                                <Oval visible height="40" width="40" color="#E04343" secondaryColor="#FCBF94" ariaLabel="loading" />
                            </div>
                        )}
                        {!loader && (
                            <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-2">
                                <thead className="text-[11px] text-[#030229] bg-[#FAFAFA] uppercase">
                                    <tr>
                                        <th className="px-2 py-2">#</th>
                                        <th className="px-2 py-2">Fleet</th>
                                        <th className="px-2 py-2">Amount</th>
                                        <th className="px-2 py-2">Reason</th>
                                        <th className="px-2 py-2">Ref</th>
                                        <th className="px-2 py-2">Status</th>
                                        <th className="px-2 py-2">Initiated By</th>
                                        <th className="px-2 py-2">L1</th>
                                        <th className="px-2 py-2">L2</th>
                                        <th className="px-2 py-2">Date</th>
                                        <th className="px-4 py-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests?.data?.length > 0 ? requests.data.map((r: any, i: number) => (
                                        <tr key={r.id} className="bg-white hover:bg-gray-50">
                                            <td className="px-2 py-3 text-xs">{i + 1}</td>
                                            <td className="px-2 py-3">
                                                <div className="text-xs font-semibold">{r.fleet_user?.first_name}</div>
                                                <div className="text-[10px] text-gray-400">{r.fleet_user?.email}</div>
                                            </td>
                                            <td className="px-2 py-3 text-xs font-bold text-gray-800">{formatAmount(r.amount)}</td>
                                            <td className="px-2 py-3 text-xs max-w-[150px] truncate" title={r.reason}>{r.reason}</td>
                                            <td className="px-2 py-3 text-xs text-gray-500">{r.reference || '—'}</td>
                                            <td className="px-2 py-3">
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${getStatusBadge(r.status)}`}>
                                                    {getStatusLabel(r.status)}
                                                </span>
                                            </td>
                                            <td className="px-2 py-3 text-xs">{r.initiator?.first_name || '—'}</td>
                                            <td className="px-2 py-3 text-xs">
                                                {r.l1_approver ? (
                                                    <span className="text-green-600" title={`Approved: ${r.approved_l1_at}`}>
                                                        {r.l1_approver.first_name} ✓
                                                    </span>
                                                ) : r.status === 'rejected' && r.rejector ? (
                                                    <span className="text-red-600">{r.rejector.first_name} ✗</span>
                                                ) : (
                                                    <span className="text-gray-300">—</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-3 text-xs">
                                                {r.l2_approver ? (
                                                    <span className="text-green-600" title={`Approved: ${r.approved_l2_at}`}>
                                                        {r.l2_approver.first_name} ✓
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-300">—</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-3 text-[10px] text-gray-500">
                                                {r.created_at ? new Date(r.created_at).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-2 flex-wrap">
                                                    <button onClick={() => { setDetailItem(r); setDetailModal(true); }}
                                                        className="text-gray-500 hover:underline text-xs">View</button>

                                                    {r.status === 'pending' && canApproveL1 && r.initiated_by !== userLoginData?.data?.id && (
                                                        <button onClick={() => openApprove(r, 'l1')}
                                                            className="text-blue-600 hover:underline text-xs font-semibold">Approve L1</button>
                                                    )}

                                                    {r.status === 'approved_l1' && canApproveL2
                                                        && r.initiated_by !== userLoginData?.data?.id
                                                        && r.approved_l1_by !== userLoginData?.data?.id && (
                                                            <button onClick={() => openApprove(r, 'l2')}
                                                                className="text-green-600 hover:underline text-xs font-semibold">Approve L2</button>
                                                        )}

                                                    {(r.status === 'pending' || r.status === 'approved_l1')
                                                        && (canApproveL1 || canApproveL2)
                                                        && r.initiated_by !== userLoginData?.data?.id && (
                                                            <button onClick={() => { setRejectId(r.id); setRejectModal(true); }}
                                                                className="text-red-600 hover:underline text-xs font-semibold">Reject</button>
                                                        )}
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan={11} className="text-center py-8 text-gray-400">No reimbursement requests found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* ── Pagination ────────────────────────────────── */}
                    <div className="flex justify-end mt-4">
                        {requests?.links?.map((link: any, idx: number) => (
                            <button key={idx} onClick={() => paginate(link.label)} disabled={link.active || !link.url}
                                className={`mx-0.5 py-1 px-2 text-xs ${link.active ? 'bg-[#E04343] text-white' : link.url ? 'bg-gray-200 text-black' : 'bg-gray-100 text-gray-400'}`}>
                                {link.label.replace('&laquo; Previous', '< Prev').replace('Next &raquo;', 'Next >')}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Create Modal ──────────────────────────────────── */}
            <Modal visible={createModal} width="480" height="480" effect="fadeInUp" onClickAway={() => setCreateModal(false)}>
                <div className="p-5 h-full overflow-auto">
                    <div className="flex justify-between mb-4">
                        <p className="font-bold text-[15px]">New Reimbursement</p>
                        <p className="cursor-pointer" onClick={() => setCreateModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>

                    <div className="mb-3">
                        <label className="text-sm text-gray-600 mb-1 block">Fleet Agent / Rider *</label>
                        <Select
                            options={fleetDropdown.map((f: any) => ({ value: f.id, label: f.label, type: f.type }))}
                            value={fleetDropdown.filter((f: any) => String(f.id) === String(newRequest.fleet_user_id)).map((f: any) => ({ value: f.id, label: f.label, type: f.type }))[0] || null}
                            onChange={(selected: any) => setNewRequest({ ...newRequest, fleet_user_id: selected ? String(selected.value) : '' })}
                            isClearable
                            isSearchable
                            isLoading={fleetDropdownLoader}
                            placeholder="Search fleet agent or rider..."
                            noOptionsMessage={() => "No matching fleet or rider found"}
                            formatOptionLabel={(option: any) => (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm">{option.label}</span>
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${option.type === 'Fleet Agent' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                        {option.type}
                                    </span>
                                </div>
                            )}
                            classNamePrefix="select"
                            menuPlacement="auto"
                            maxMenuHeight={200}
                            styles={{
                                control: (base: any) => ({ ...base, borderColor: '#D1D1D1', borderRadius: '6px', fontSize: '14px', minHeight: '40px' }),
                                placeholder: (base: any) => ({ ...base, fontSize: '13px', color: '#DADADA' }),
                                option: (base: any) => ({ ...base, fontSize: '13px', padding: '8px 12px' }),
                                menuList: (base: any) => ({ ...base, maxHeight: '200px' }),
                            }}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="text-sm text-gray-600 mb-1 block">Amount (₦) *</label>
                        <input type="number" min="1" value={newRequest.amount}
                            onChange={(e) => setNewRequest({ ...newRequest, amount: e.target.value })}
                            className="w-full border border-gray-300 rounded-md p-2 text-sm" placeholder="0.00" />
                    </div>

                    <div className="mb-3">
                        <label className="text-sm text-gray-600 mb-1 block">Reason *</label>
                        <textarea value={newRequest.reason}
                            onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })}
                            className="w-full border border-gray-300 rounded-md p-2 text-sm" rows={3}
                            placeholder="Why is this reimbursement needed?" />
                    </div>

                    <div className="mb-4">
                        <label className="text-sm text-gray-600 mb-1 block">Reference (optional)</label>
                        <input type="text" value={newRequest.reference}
                            onChange={(e) => setNewRequest({ ...newRequest, reference: e.target.value })}
                            className="w-full border border-gray-300 rounded-md p-2 text-sm" placeholder="Receipt #, invoice #, etc." />
                    </div>

                    <button onClick={handleCreate} disabled={actionLoader}
                        className="bg-[#E04343] text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2">
                        Create Request
                        <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            {/* ── Approve Modal ─────────────────────────────────── */}
            <Modal visible={approveModal} width="420" height="320" effect="fadeInUp" onClickAway={() => setApproveModal(false)}>
                <div className="p-5">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">{approveType === 'l1' ? 'Level 1 Approval' : 'Final Approval (Level 2)'}</p>
                        <p className="cursor-pointer" onClick={() => setApproveModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>

                    {approveItem && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-3 text-xs space-y-1">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Fleet:</span>
                                <span className="font-semibold">{approveItem.fleet_user?.first_name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Amount:</span>
                                <span className="font-bold text-green-700">{formatAmount(approveItem.amount)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Reason:</span>
                                <span className="max-w-[200px] text-right">{approveItem.reason}</span>
                            </div>
                            {approveType === 'l2' && approveItem.l1_approver && (
                                <div className="flex justify-between">
                                    <span className="text-gray-500">L1 Approved by:</span>
                                    <span className="text-blue-600 font-semibold">{approveItem.l1_approver.first_name}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {approveType === 'l2' && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-3 text-xs text-green-800">
                            This will credit <strong>{formatAmount(approveItem?.amount)}</strong> to the fleet's wallet immediately.
                        </div>
                    )}

                    <div className="mb-4">
                        <label className="text-sm text-gray-600 mb-1 block">Notes (optional)</label>
                        <textarea value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)}
                            className="w-full border border-gray-300 rounded-md p-2 text-sm" rows={2}
                            placeholder="Any notes for this approval..." />
                    </div>

                    <button onClick={handleApprove} disabled={actionLoader}
                        className={`${approveType === 'l2' ? 'bg-green-600' : 'bg-blue-600'} text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2`}>
                        {approveType === 'l1' ? 'Approve Level 1' : 'Approve & Credit Wallet'}
                        <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            {/* ── Reject Modal ──────────────────────────────────── */}
            <Modal visible={rejectModal} width="400" height="250" effect="fadeInUp" onClickAway={() => setRejectModal(false)}>
                <div className="p-4">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">Reject Reimbursement</p>
                        <p className="cursor-pointer" onClick={() => setRejectModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    <div className="mb-4">
                        <label className="text-sm text-gray-600 mb-1 block">Reason *</label>
                        <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                            className="w-full border border-gray-300 rounded-md p-2 text-sm" rows={3}
                            placeholder="Why is this reimbursement being rejected?" />
                    </div>
                    <button onClick={handleReject} disabled={actionLoader}
                        className="bg-red-600 text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2">
                        Reject
                        <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            {/* ── Detail Modal ──────────────────────────────────── */}
            <Modal visible={detailModal} width="520" height="520" effect="fadeInUp" onClickAway={() => setDetailModal(false)}>
                <div className="p-5 h-full overflow-auto">
                    <div className="flex justify-between mb-4">
                        <p className="font-bold text-[15px]">Reimbursement Details</p>
                        <p className="cursor-pointer" onClick={() => setDetailModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>

                    {detailItem && (
                        <div className="space-y-4">
                            {/* Status banner */}
                            <div className={`rounded-lg p-3 text-center ${getStatusBadge(detailItem.status)}`}>
                                <p className="text-sm font-bold">{getStatusLabel(detailItem.status)}</p>
                            </div>

                            {/* Amount */}
                            <div className="text-center">
                                <p className="text-3xl font-bold text-gray-800">{formatAmount(detailItem.amount)}</p>
                            </div>

                            {/* Details grid */}
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-xs">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Fleet Agent</span>
                                    <span className="font-semibold">{detailItem.fleet_user?.first_name} ({detailItem.fleet_user?.email})</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Reason</span>
                                    <span className="max-w-[250px] text-right">{detailItem.reason}</span>
                                </div>
                                {detailItem.reference && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Reference</span>
                                        <span>{detailItem.reference}</span>
                                    </div>
                                )}
                            </div>

                            {/* Approval trail */}
                            <div className="bg-white border rounded-lg p-4 space-y-3">
                                <p className="text-xs font-bold text-gray-700 uppercase">Approval Trail</p>

                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 flex-shrink-0">1</div>
                                    <div>
                                        <p className="text-xs font-semibold">Initiated</p>
                                        <p className="text-[10px] text-gray-500">{detailItem.initiator?.first_name} {detailItem.initiator?.last_name} — {detailItem.initiated_at ? new Date(detailItem.initiated_at).toLocaleString() : '—'}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${detailItem.approved_l1_by ? 'bg-blue-200 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>2</div>
                                    <div>
                                        <p className="text-xs font-semibold">Level 1 Approval</p>
                                        {detailItem.l1_approver ? (
                                            <p className="text-[10px] text-blue-600">{detailItem.l1_approver.first_name} — {new Date(detailItem.approved_l1_at).toLocaleString()}{detailItem.l1_notes && ` — "${detailItem.l1_notes}"`}</p>
                                        ) : detailItem.status === 'rejected' ? (
                                            <p className="text-[10px] text-red-500">Skipped (rejected)</p>
                                        ) : (
                                            <p className="text-[10px] text-gray-400">Pending</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${detailItem.approved_l2_by ? 'bg-green-200 text-green-700' : 'bg-gray-100 text-gray-400'}`}>3</div>
                                    <div>
                                        <p className="text-xs font-semibold">Level 2 Approval & Wallet Credit</p>
                                        {detailItem.l2_approver ? (
                                            <p className="text-[10px] text-green-600">{detailItem.l2_approver.first_name} — {new Date(detailItem.approved_l2_at).toLocaleString()}{detailItem.l2_notes && ` — "${detailItem.l2_notes}"`}</p>
                                        ) : detailItem.status === 'rejected' ? (
                                            <p className="text-[10px] text-red-500">Skipped (rejected)</p>
                                        ) : (
                                            <p className="text-[10px] text-gray-400">Pending</p>
                                        )}
                                    </div>
                                </div>

                                {detailItem.status === 'rejected' && detailItem.rejector && (
                                    <div className="bg-red-50 rounded-lg p-3 mt-2">
                                        <p className="text-xs font-bold text-red-700">Rejected</p>
                                        <p className="text-[10px] text-red-600">
                                            By {detailItem.rejector.first_name} — {detailItem.rejected_at ? new Date(detailItem.rejected_at).toLocaleString() : '—'}
                                        </p>
                                        <p className="text-[10px] text-red-500 mt-1">Reason: {detailItem.rejection_reason}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar />
        </>
    );
}

export default Reimbursements;